import Grid from '@material-ui/core/Grid'
import React, { FC } from 'react'
import { RichTextWrapper } from '../../../styles/global'
import { BioHero } from './style'

//* utils
import { UseCaseTypes, blogUseCaseUrlMappings } from '../../../constants/blogUseCase'
import { BioLinkedIn, BlogTwitter } from '../../../constants/svg'
import { ComponentBio } from '../../../models/components/bio'
import { generateBlogPostSlug, generateSecondaryBlogSlug } from '../../../utils/blog.utils'
import ImgUtil from '../../../utils/img.utils'
import { renderRichText } from '../../../utils/richTextHandler.utils'

//* components
import Breadcrumbs from '../breadcrumbs'
import GlobalContainer from '../globalContainer'
import Typography from '../typography'
import WrapperImage from '../wrapperImage'
import { ByLine } from './subcomponents/byLine'
import { Description } from './subcomponents/description'

type Props = {
  data: ComponentBio
  type?: 'bioShortDescription' | 'bioHero' | 'bioByline'
  className?: string
  location?: any
  pageTitle?: string
  locale?: string
  isSecondaryBlog?: boolean
  useCase?: string
}

const Bio: FC<Props> = ({
  data,
  type = 'bioHero',
  className,
  isSecondaryBlog,
  locale,
  useCase = '',
}) => {
  if (!data) return null
  const cn = className ? { className: className } : {}
  const bio = {
    firstName: data.firstName || '',
    lastName: data.lastName || '',
    role: data.role || '',
    company: data.company || '',
    miniBio: data.miniBio || '',
    twitter: data.twitterProfile || '',
    linkedIn: data.linkedInProfile || '',
    headshot: {
      url: data.headshot?.image?.file?.url || '',
      alt: data.headshot?.altText || '',
    },
  }

  let authorSlug = `${bio.firstName} ${bio.lastName}`
    .toLowerCase()
    .replace(/ *\([^)]*\) */g, '')
    .replace(/\s/g, '-')

  let href = ''
  if (isSecondaryBlog) {
    href = generateSecondaryBlogSlug({
      slug: '',
      blogCategoryName: authorSlug,
      useCase,
      pageLocale: locale,
    })
  } else {
    href = generateBlogPostSlug({
      slug: '',
      blogCategoryName: authorSlug,
      pageLocale: locale,
    })
  }

  switch (type) {
    case 'bioShortDescription':
      return <Description cn={cn} bio={bio} href={href} />
    case 'bioByline':
      return <ByLine cn={cn} bio={bio} href={href} />
    case 'bioHero':
      const pathname =
        useCase !== UseCaseTypes.MAIN_BLOG ? `/${blogUseCaseUrlMappings[useCase]}/` : `/blog`
      return (
        <BioHero {...cn} data-cy="bioHero">
          <GlobalContainer className="BioHero__Outer-Container">
            <Breadcrumbs
              backgroundTheme="light"
              currentPathname={pathname}
              pageName={`${bio.firstName} ${bio.lastName}`}
              className="BioHero__Breadcrumb"
              isBlogBreadcrumb={true}
              locale={locale}
              isSecondaryBlog={isSecondaryBlog}
            />

            <Grid container className="BioHero__Inner-Container" spacing={2}>
              <Grid item className="BioHero__Text-Container" sm={6} md={6} lg={5}>
                {(bio.firstName || bio.lastName) && (
                  <Typography type="header1">
                    {bio.firstName} {bio.lastName}
                  </Typography>
                )}

                {(bio.role || bio.company) && (
                  <Typography type="header6">
                    {`${bio.role} ${
                      bio.company ? (bio.role ? `- ${bio.company}` : bio.company) : ''
                    }`}
                  </Typography>
                )}

                {bio.miniBio && (
                  <RichTextWrapper $type="body" className="BioHero__Body font__inter">
                    {renderRichText(bio.miniBio)}
                  </RichTextWrapper>
                )}

                {(bio.linkedIn || bio.twitter) && (
                  <div className="BioHero__Social-Container">
                    <Typography type="label">Follow on:</Typography>
                    {bio.twitter && (
                      <a href={bio.twitter} target="_blank">
                        <BlogTwitter />
                      </a>
                    )}
                    {bio.linkedIn && (
                      <a href={bio.linkedIn} target="_blank">
                        <BioLinkedIn />
                      </a>
                    )}
                  </div>
                )}
              </Grid>

              {bio.headshot.url && (
                <div className="BioHero__Image-Container">
                  <WrapperImage
                    className="BioHero__Image-Author"
                    src={ImgUtil.getResizedImageUrl(bio.headshot.url, '')}
                    alt={bio.headshot.alt}
                    width={265}
                    height={265}
                    threshold={650}
                  />
                </div>
              )}
            </Grid>
          </GlobalContainer>
        </BioHero>
      )
    default:
      return null
  }
}

export default Bio
